import React, { useEffect, useState } from "react";
import bookLoader from "../../../../../assets/lotties/BookLoader.json";
import noData from "../../../../../assets/lotties/guidePageNoData.json";
import Lottie from "react-lottie";
import {
  getGuideContentApi,
  getFreeGuidesContentApi,
  guideMarkAsCompleteApi,
  freeGuideMarkAsCompleteApi,
  regenerateContentApi,
} from "../../../../../api/candidate";
import WebUtils from "../../../../../utils/WebUtils";
import Utils from "../../../../../utils/Utils";
import { Skeleton } from "antd";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import "../../../../../styles/markdown.css";
import remarkGfm from "remark-gfm";
import MiddleViewQuestionBank from "./MiddleViewQuestionBank";
import { RotateCcw } from "lucide-react";

const userDetail = Utils.getUserDetail();

const loadingLottiFile = {
  loop: true,
  autoplay: true,
  animationData: bookLoader,
};
const noDataJSON = {
  loop: false,
  autoplay: true,
  animationData: noData,
};

const markdownDummy = `# Markdown syntax guide

## Headers

# This is a Heading h1
## This is a Heading h2
###### This is a Heading h6

## Emphasis

*This text will be italic*  
_This will also be italic_

**This text will be bold**  
__This will also be bold__

_You **can** combine them_

## Lists

### Unordered

* Item 1
* Item 2
* Item 2a
* Item 2b
    * Item 3a
    * Item 3b

### Ordered

1. Item 1
2. Item 2
3. Item 3
    1. Item 3a
    2. Item 3b

## Images

![This is an alt text.](https://images.unsplash.com/photo-1503023345310-bd7c1de61c7d?w=800 "This is a sample image.")


## Links

You may be using [Markdown Live Preview](https://markdownlivepreview.com/).

## Blockquotes

> Markdown is a lightweight markup language with plain-text-formatting syntax, created in 2004 by John Gruber with Aaron Swartz.
>
>> Markdown is often used to format readme files, for writing messages in online discussion forums, and to create rich text using a plain text editor.

## Tables

| Left columns  | Right columns |
| ------------- |:-------------:|
| left foo      | right column data of table    |
| left bar      | right column data of table no 2     |
| left baz      | right column data of table no 3     |

## Blocks of code

\`\`\`
let message = 'Hello world';
alert(message);
\`\`\`

## Inline code

This web site is using \`markedjs/marked\`
`;

function sanitizeMarkdown(content = "") {
  // Trim leading and trailing triple backticks
  if (content.startsWith("```") && content.endsWith("```")) {
    content = content.slice(3, -3).trim();
  }
  if (content.startsWith("```")) {
    content = content.slice(3).trim();
  }

  // Remove "markdown" if it's the first word
  if (content.startsWith("markdown")) {
    content = content.slice(8).trim();
  }

  return content;
}

function MiddleViewGuide({
  guideDetails,
  currTopicId,
  currSubTopicId,
  jobGuideIdentifier,
  setGuideDetails,
  industryId,
  setOpen,
  open,
  setActiveIndex,
  activeIndex,
  buttons,
  currentSkills,
  setCurrentSkills,
  skillRegenerateFlag,
  setSkillRegenerateFlag,
  qbSkillType,
  setQBSkillType,
}) {
  const [loading, setLoading] = useState(false);
  const [contentData, setContentData] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const [contentGeneratedFlag, setContentGeneratedFlag] = useState(false);
  const [contentRefressFlag, setContentRefressFlag] = useState(false);

  function checkSubTopicCompletion(data, subTopicIdentifier) {
    for (const topic of data.jobTopicList) {
      for (const subTopic of topic.jobSubTopic) {
        if (subTopic.subTopicIdentifier === subTopicIdentifier) {
          return {
            found: true,
            completed: subTopic.completed,
          };
        }
      }
    }
    return { found: false, completed: null };
  }

  const updateCompletionStatus = (
    data,
    topicIdentifier,
    subTopicIdentifier,
    newStatus,
  ) => {
    // Clone the data to avoid direct mutations
    const updatedData = { ...data };

    // Find the topic with the given topicIdentifier
    const topic = updatedData.jobTopicList.find(
      (topic) => topic.topicIdentifier === topicIdentifier,
    );

    if (topic) {
      const subTopic = topic.jobSubTopic.find(
        (subTopic) => subTopic.subTopicIdentifier === subTopicIdentifier,
      );

      if (subTopic) {
        subTopic.completed = newStatus;
      }
    }

    setGuideDetails(updatedData);
  };

  useEffect(() => {
    if (currTopicId && currSubTopicId && jobGuideIdentifier) {
      getGuideContent();
      const checkCompleteStatus = checkSubTopicCompletion(
        guideDetails,
        currSubTopicId,
      );
      if (checkCompleteStatus.completed) {
        setIsCompleted(true);
      } else {
        setIsCompleted(false);
      }
    }
  }, [currTopicId, currSubTopicId]);

  const getGuideContent = () => {
    setContentRefressFlag(false);
    let url = "";
    if (industryId) {
      url = `${getFreeGuidesContentApi}?userIdentifier=${
        userDetail?.userIdentifier || null
      }&jobGuideIdentifier=${jobGuideIdentifier}&industryId=${industryId}&topicIdentifier=${currTopicId}&subTopicIdentifier=${currSubTopicId}`;
    } else {
      url = `${getGuideContentApi}?userIdentifier=${
        userDetail?.userIdentifier || null
      }&jobGuideIdentifier=${jobGuideIdentifier}&topicIdentifier=${currTopicId}&subTopicIdentifier=${currSubTopicId}&organizationId=${userDetail.organizationId}`;
    }

    setLoading(true);

    WebUtils.httpOperations(url, {}, "GET")
      .then((result) => {
        setLoading(false);
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          if (result.data.data.subTopicContent) {
            const rawContent =
              result.data.data.subTopicContent?.contentValue?.output || "";
            const sanitizedContent = sanitizeMarkdown(rawContent);
            setContentData(sanitizedContent);
          } else {
            setContentData("");
          }
          setTimeout(() => {
            setContentRefressFlag(true);
          }, 45000);
          setContentGeneratedFlag(result.data.data.subTopicContentGenerated);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };
  const markCompleteHandler = (isComplete) => {
    setLoading(true);

    let url = "";
    if (industryId) {
      url = `${freeGuideMarkAsCompleteApi}?userIdentifier=${
        userDetail?.userIdentifier || null
      }&industryId=${industryId}&jobGuideIdentifier=${jobGuideIdentifier}&topicIdentifier=${currTopicId}&subTopicIdentifier=${currSubTopicId}&isCompleted=${isComplete}`;
    } else {
      url = `${guideMarkAsCompleteApi}?userIdentifier=${
        userDetail?.userIdentifier || null
      }&jobGuideIdentifier=${jobGuideIdentifier}&topicIdentifier=${currTopicId}&subTopicIdentifier=${currSubTopicId}&isCompleted=${isComplete}`;
    }

    WebUtils.httpOperations(url, {}, "PUT")
      .then((result) => {
        setLoading(false);
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          updateCompletionStatus(
            guideDetails,
            currTopicId,
            currSubTopicId,
            isComplete,
          );
          if (isComplete) {
            setIsCompleted(true);
          } else {
            setIsCompleted(false);
          }
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };
  const handleRegenerateContent = () => {
    setLoading(true);
    const url = `${regenerateContentApi}?userIdentifier=${
      userDetail?.userIdentifier || null
    }&jobGuideIdentifier=${jobGuideIdentifier}&topicIdentifier=${currTopicId}&subTopicIdentifier=${currSubTopicId}`;

    WebUtils.httpOperations(url, {}, "PUT")
      .then((result) => {
        setLoading(false);
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          if (result.data.data.subTopicContent)
            setContentData(result.data.data.subTopicContent?.contentValue);
          else setContentData("");
          setContentGeneratedFlag(result.data.data?.subTopicContentGenerated);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  return (
    <>
      {loading ? (
        <div className="h-[87vh] flex flex-col gap-3">
          <Skeleton active className="p-3" />
          <Skeleton active className="p-3" />
          <Skeleton active className="p-3" />
        </div>
      ) : (
        <div className="driver-single-guide-generated-guide-content">
          <div className="h-[87vh] p-[1.2rem] sm:px-[1.8rem] pt-[1rem] pb-[.8rem] shadow rounded-xl border border-[#D0D5DD] bg-[#FCFCFD] flex gap-y-2 flex-col justify-between">
            {/* Display QB UI if skills are being generated or no skills or there is current selected skill */}
            {skillRegenerateFlag?.skillsBeingGenerated ||
            skillRegenerateFlag?.noSkillsPresent ||
            currentSkills ? (
              <MiddleViewQuestionBank
                userDetail={userDetail}
                jobGuideIdentifier={jobGuideIdentifier}
                buttons={buttons}
                activeIndex={activeIndex}
                open={open}
                setActiveIndex={setActiveIndex}
                setOpen={setOpen}
                currentSkills={currentSkills}
                setCurrentSkills={setCurrentSkills}
                skillRegenerateFlag={skillRegenerateFlag}
                setSkillRegenerateFlag={setSkillRegenerateFlag}
                qbSkillType={qbSkillType}
                setQBSkillType={setQBSkillType}
                industryId={industryId}
              />
            ) : !contentGeneratedFlag ? (
              <div className="h-full flex flex-col justify-center items-center">
                <div className="w-full mx-auto">
                  <Lottie
                    style={{
                      width: "100%",
                      maxWidth: "180px",
                      cursor: "default",
                    }}
                    options={loadingLottiFile}
                  />
                </div>
                <div className="text-[#667085] flex flex-col gap-y-2 text-[1.15rem] mx-auto text-center mt-[-1.2rem]">
                  <span className="max-w-[450px]">
                    Hang tight! We're generating your content.
                    {/* <span className="dots text-left inline-block w-[10px]"></span> */}
                  </span>
                  {contentRefressFlag && (
                    <span className="text-sm flex items-center gap-2 cursor-pointer text-center justify-center hover:text-gray-600">
                      <span>
                        If it takes more than a minute,{" "}
                        <span
                          onClick={getGuideContent}
                          className="text-[#F79009] border-b border-b-[#F79009] hover:brightness-105 cursor-pointer font-medium"
                        >
                          click here to refresh.{" "}
                        </span>
                      </span>
                    </span>
                  )}
                </div>
              </div>
            ) : contentData ? (
              <>
                <div className="text-[#475467] text-[0.94rem] mb-3 h-full overflow-y-auto border-b-2 pr-2 py-2 orange-scroll markdown-container">
                  <Markdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkBreaks, remarkGfm]}
                  >
                    {contentData}
                    {/* {markdownDummy} */}
                  </Markdown>
                </div>
                <div className="flex justify-between sm:flex-row flex-col gap-y-2">
                  <span>
                    {isCompleted ? (
                      <button
                        className="btn-white px-[0.9rem] py-[0.45rem] text-[0.9rem] flex gap-2 items-center"
                        onClick={() => markCompleteHandler(false)}
                      >
                        <svg
                          width="18"
                          height="15"
                          viewBox="0 0 20 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 5.5H14.5C16.9853 5.5 19 7.51472 19 10C19 12.4853 16.9853 14.5 14.5 14.5H10M1 5.5L5 1.5M1 5.5L5 9.5"
                            stroke="#475467"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                        Undo completion
                      </button>
                    ) : (
                      <button
                        className="btn-orange px-[1rem] py-[0.45rem] text-[0.9rem] flex gap-[0.6rem] items-center"
                        onClick={() => markCompleteHandler(true)}
                      >
                        <svg
                          width="17"
                          height="12"
                          viewBox="0 0 19 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17.5 1.5L6.5 12.5L1.5 7.5"
                            stroke="black"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                        Mark as done
                      </button>
                    )}
                  </span>
                  <div className="flex justify-end">
                    <span className="border-2 rounded-md px-2 py-1 flex  gap-x-1 items-center">
                      {buttons.map((btn, index) => {
                        // if (index === 1) return null;

                        const isActive = activeIndex === index && open;

                        return (
                          <button
                            key={index}
                            onClick={() => {
                              if (isActive) {
                                setOpen(false);
                                setActiveIndex(null);
                              } else {
                                setActiveIndex(index);
                                setOpen(true);
                              }
                            }}
                            className={`
        px-3 py-[6px] text-[0.9rem] flex gap-[0.6rem] items-center
        rounded-md transition-colors duration-200
        ${activeIndex === index ? "btn-orange" : ""}
        hover:bg-amber-300
      `}
                          >
                            {btn.id === 0 && (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                {" "}
                                <path
                                  d="M13.6663 5.41667C13.6663 4.56419 13.6663 4.13796 13.5726 3.78825C13.3184 2.83924 12.5771 2.09799 11.6281 1.8437C11.2784 1.75 10.8521 1.75 9.99967 1.75C9.1472 1.75 8.72096 1.75 8.37126 1.8437C7.42225 2.09799 6.681 2.83924 6.42671 3.78825C6.33301 4.13796 6.33301 4.56419 6.33301 5.41667M3.76634 18.25H16.233C17.2598 18.25 17.7732 18.25 18.1653 18.0502C18.5103 17.8744 18.7908 17.5939 18.9665 17.249C19.1663 16.8568 19.1663 16.3434 19.1663 15.3167V8.35C19.1663 7.32324 19.1663 6.80986 18.9665 6.41768C18.7908 6.07272 18.5103 5.79226 18.1653 5.61649C17.7732 5.41667 17.2598 5.41667 16.233 5.41667H3.76634C2.73958 5.41667 2.2262 5.41667 1.83403 5.61649C1.48906 5.79226 1.2086 6.07272 1.03283 6.41768C0.833008 6.80986 0.833008 7.32324 0.833008 8.35V15.3167C0.833008 16.3434 0.833008 16.8568 1.03283 17.249C1.2086 17.5939 1.48906 17.8744 1.83403 18.0502C2.2262 18.25 2.73958 18.25 3.76634 18.25Z"
                                  stroke="#0F1B23"
                                  stroke-width="1.67"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />{" "}
                              </svg>
                            )}{" "}
                            {btn.id === 1 && (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                {" "}
                                <path
                                  d="M17 14.8318L16.111 15.8043C15.6395 16.3199 15.0001 16.6096 14.3335 16.6096C13.6668 16.6096 13.0274 16.3199 12.5559 15.8043C12.0838 15.2897 11.4444 15.0008 10.7779 15.0008C10.1115 15.0008 9.47213 15.2897 8.99999 15.8043M1 16.6096H2.48848C2.92331 16.6096 3.14073 16.6096 3.34533 16.5605C3.52673 16.5169 3.70014 16.4451 3.8592 16.3476C4.03861 16.2377 4.19234 16.0839 4.49981 15.7765L15.6667 4.60958C16.4031 3.8732 16.4031 2.67929 15.6667 1.94291C14.9303 1.20653 13.7364 1.20653 13 1.94291L1.83312 13.1098C1.52565 13.4173 1.37192 13.571 1.26197 13.7504C1.1645 13.9095 1.09267 14.0829 1.04912 14.2643C1 14.4689 1 14.6863 1 15.1211V16.6096Z"
                                  stroke="#475467"
                                  stroke-width="1.67"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />{" "}
                              </svg>
                            )}{" "}
                            {btn.id === 2 && (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                {" "}
                                <path
                                  d="M7.70833 7.21822C7.70833 6.78071 7.70833 6.56195 7.79976 6.43983C7.87944 6.3334 8.0014 6.26682 8.13401 6.25734C8.28619 6.24648 8.4702 6.36477 8.83822 6.60136L13.1654 9.38314C13.4848 9.58843 13.6445 9.69108 13.6996 9.82161C13.7478 9.93565 13.7478 10.0643 13.6996 10.1784C13.6445 10.3089 13.4848 10.4116 13.1654 10.6169L8.83822 13.3986C8.4702 13.6352 8.28619 13.7535 8.13401 13.7427C8.0014 13.7332 7.87944 13.6666 7.79976 13.5602C7.70833 13.438 7.70833 13.2193 7.70833 12.7818V7.21822Z"
                                  stroke="#475467"
                                  stroke-width="1.67"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />{" "}
                                <path
                                  d="M1.75 6.15C1.75 4.60986 1.75 3.83978 2.04973 3.25153C2.31338 2.73408 2.73408 2.31338 3.25153 2.04973C3.83978 1.75 4.60986 1.75 6.15 1.75H13.85C15.3901 1.75 16.1602 1.75 16.7485 2.04973C17.2659 2.31338 17.6866 2.73408 17.9503 3.25153C18.25 3.83978 18.25 4.60986 18.25 6.15V13.85C18.25 15.3901 18.25 16.1602 17.9503 16.7485C17.6866 17.2659 17.2659 17.6866 16.7485 17.9503C16.1602 18.25 15.3901 18.25 13.85 18.25H6.15C4.60986 18.25 3.83978 18.25 3.25153 17.9503C2.73408 17.6866 2.31338 17.2659 2.04973 16.7485C1.75 16.1602 1.75 15.3901 1.75 13.85V6.15Z"
                                  stroke="#475467"
                                  stroke-width="1.67"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />{" "}
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="h-full flex flex-col justify-center items-center">
                <div className="w-full">
                  <Lottie
                    style={{ width: "100%", maxWidth: "300px" }}
                    options={noDataJSON}
                  />
                </div>
                <div className="text-[#667085] text-[1.1rem] mx-auto text-center mt-[-2.2rem]">
                  No content found.
                </div>
                <button
                  className="btn-orange px-[1rem] py-[0.45rem] text-[0.9rem] mt-5 flex gap-[0.6rem] items-center"
                  onClick={handleRegenerateContent}
                >
                  Regenerate Content
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default MiddleViewGuide;
